import { useState } from "react";
import { toast } from "sonner";
import { BrainCircuit, Crown, Loader2 } from "lucide-react";
import { useFetch, Panel, Tag, PageHeader, dirTone, Empty } from "@/components/Common";
import { api, errMsg, fmtPrice, fmtTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DESKS = [
  { key: "forex", label: "FX Desk", symbols: ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCHF", "USDCAD"] },
  { key: "gold", label: "Metals Desk", symbols: ["XAUUSD"] },
  { key: "stocks", label: "Equities Desk", symbols: ["AAPL", "MSFT", "NVDA", "TSLA", "AMZN", "SPY"] },
];

function Result({ r }) {
  const j = r.journal, ai = j.ai || {};
  const sym = j.symbol;
  return (
    <div className="space-y-3 rounded border bg-secondary/40 p-4 fade-up" data-testid={`analysis-result-${sym}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{sym}</span>
        <Tag tone={dirTone(ai.decision)} testid="analysis-decision">AI {ai.decision || "n/a"}</Tag>
        <Tag tone={j.strategy ? "up" : "muted"}>{j.strategy || "no setup"}</Tag>
        <Tag tone="blue">bias {j.bias || "none"}</Tag>
        <Tag>{j.regime?.regime}</Tag><Tag>{j.data_source}</Tag>
        <Tag tone={j.action === "rejected" ? "warn" : "up"} testid="analysis-stage">{j.stage}: {j.action}</Tag>
      </div>
      {j.reasons?.length > 0 && <p className="font-mono text-xs text-[hsl(var(--warn))]" data-testid="analysis-reasons">{j.reasons.join(" · ")}</p>}
      <p className="text-sm text-muted-foreground" data-testid="analysis-reasoning">{ai.reasoning}</p>
      {ai.news_assessment && <p className="text-xs text-muted-foreground"><span className="label">News </span>{ai.news_assessment}</p>}
      {j.direction && (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div><div className="label">Signal</div><div className="num">{j.direction} {fmtPrice(j.signal_price, sym)}</div></div>
          <div><div className="label">Stop</div><div className="num text-down">{fmtPrice(j.stop_loss, sym)}</div></div>
          <div><div className="label">Target</div><div className="num text-up">{fmtPrice(j.take_profit, sym)}</div></div>
        </div>
      )}
      <div className="flex flex-wrap gap-1">{j.timeframes?.map((s) => <Tag key={s.timeframe} tone={s.score > 0 ? "up" : s.score < 0 ? "down" : "muted"}>{s.timeframe} {s.trend} {s.score}</Tag>)}</div>
      <div className="flex flex-wrap gap-1">{j.votes?.map((v) => <Tag key={v.name} tone={v.valid ? "up" : "muted"}>{v.name}{v.valid ? ` ${v.direction}` : " ✕"}</Tag>)}</div>
      {j.quote && <p className="font-mono text-[10px] text-muted-foreground">Quote {fmtPrice(j.quote.mid, sym)} · spread {j.quote.spread_bps}bps ({j.quote.spread_source}) · bar {fmtTime(j.quote.market_timestamp)}</p>}
      {ai.key_risks?.length > 0 && <ul className="list-disc pl-4 text-xs text-[hsl(var(--warn))]">{ai.key_risks.map((k) => <li key={k}>{k}</li>)}</ul>}
    </div>
  );
}

function Desk({ desk }) {
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);
  const run = async (s) => {
    setBusy(s);
    try { const { data } = await api.post(`/analysis/${s}`); setRes(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <Panel title={desk.label} testid={`ai-desk-${desk.key}-card`} right={<BrainCircuit size={16} className="text-primary" />}>
      <div className="flex flex-wrap gap-2">
        {desk.symbols.map((s) => (
          <Button key={s} size="sm" variant="outline" disabled={!!busy} onClick={() => run(s)} data-testid={`analyze-${s}-button`} className="font-mono">
            {busy === s && <Loader2 size={12} className="mr-1 animate-spin" />}{s}
          </Button>
        ))}
      </div>
      <div className="mt-4">{res ? <Result r={res} /> : <p className="text-xs text-muted-foreground">Select a symbol to run multi-timeframe technical + AI analysis. Manual analysis never places trades.</p>}</div>
    </Panel>
  );
}

function Master() {
  const { data, setData } = useFetch("/analysis/master");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/analysis/master/run"); setData(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const m = data?.report;
  return (
    <Panel title="Portfolio Control" testid="ai-desk-master-card" right={<Button size="sm" onClick={run} disabled={busy} data-testid="master-run-button">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Crown size={14} className="mr-1" />}Run portfolio review</Button>}>
      {m ? (
        <div className="space-y-3 text-sm" data-testid="master-report">
          <div className="flex flex-wrap gap-2"><Tag tone="gold">{m.market_regime}</Tag><Tag tone="blue">{m.overall_bias}</Tag><span className="font-mono text-xs text-muted-foreground">{fmtTime(data.created_at)}</span></div>
          <p className="text-muted-foreground">{m.summary}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div><div className="label mb-1">Approved</div>{m.approved?.length ? m.approved.map((a) => <Tag key={a} tone="up">{a}</Tag>) : <span className="text-xs text-muted-foreground">None — standing aside</span>}</div>
            <div><div className="label mb-1">Rejected</div>{m.rejected?.map((r) => <div key={r.symbol} className="text-xs"><span className="font-mono">{r.symbol}</span> — {r.reason}</div>)}</div>
          </div>
          {m.risk_warnings?.length > 0 && <ul className="list-disc pl-4 text-xs text-[hsl(var(--warn))]">{m.risk_warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
        </div>
      ) : <Empty>No Master report yet.</Empty>}
    </Panel>
  );
}

function Scanner() {
  const { data } = useFetch("/analysis/scans", [], 30000);
  return (
    <Panel title="Market scanner" testid="scanner-table">
      {data?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="label text-left"><th className="py-2">Symbol</th><th>Score</th><th>Bias</th><th>Regime</th><th>Strategy</th><th>Outcome</th><th>Session</th><th>Updated</th></tr></thead>
            <tbody>{[...data].sort((a, b) => Math.abs(b.score) - Math.abs(a.score)).map((s) => (
              <tr key={s.symbol} className="border-t border-border/50" data-testid={`scan-row-${s.symbol}`}>
                <td className="py-2 font-mono">{s.symbol}</td><td className="num">{s.score}</td><td><Tag tone={dirTone(s.direction)}>{s.direction}</Tag></td>
                <td>{s.regime ? <Tag>{s.regime}</Tag> : "—"}</td><td className="text-xs">{s.strategy}</td><td><Tag tone={s.action === "executed" ? "up" : s.action === "rejected" ? "warn" : "blue"}>{s.stage}: {s.action}</Tag></td>
                <td>{s.market_open ? <Tag tone="up">open</Tag> : <Tag tone="warn">closed</Tag>}</td><td className="font-mono text-xs text-muted-foreground">{fmtTime(s.analyzed_at)}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      ) : <Empty>Scanner results appear after the first engine scan or desk analysis.</Empty>}
    </Panel>
  );
}

export default function Desks() {
  return (
    <div className="space-y-4">
      <PageHeader title="Decision Desks" sub="Portfolio Control coordinates FX, Metals and Equities specialists. Machine intelligence is a veto layer on deterministic, rule-based setups — confidence scores are informational only." />
      <Master />
      <div className="grid gap-4 xl:grid-cols-3">{DESKS.map((d) => <Desk key={d.key} desk={d} />)}</div>
      <Scanner />
    </div>
  );
}
